import { Box } from "@chakra-ui/react";
import { colors } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { ReactNode } from "react";

// 무빈소 vs 가족장 비용 비교 막대그래프(장례/비용 퍼널 공용).
// 두 막대를 바닥(flex-end) 정렬. 가격 라벨/캡션에 고정 높이를 줘 바닥선을 맞춘다.

// 차트 전용 색상(시스템 팔레트에 없는 데이터-viz 색). 디자인 스펙 값.
const CHART_YELLOW = "#FFC62D"; // 조문객 음식
const CHART_SEGMENT_GRAY = colors.gray200; // 빈소·시설 이용료

const LABEL_HEIGHT = "17px";
const CAPTION_HEIGHT = "34px";
const BAR_WIDTH = "116px";

function Segment({
  height,
  bg,
  color,
  children,
}: {
  height: string;
  bg: string;
  color: string;
  children: ReactNode;
}) {
  return (
    <Box
      height={height}
      bg={bg}
      color={color}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      className="caption_12_b"
      lineHeight={1.3}
    >
      {children}
    </Box>
  );
}

function Bar({
  priceLabel,
  priceLabelColor,
  caption,
  children,
}: {
  priceLabel: string;
  priceLabelColor: string;
  caption: ReactNode;
  children: ReactNode;
}) {
  return (
    <BaseStack spacing={8} alignItems="center">
      <Box className="subtitle_14_b" color={priceLabelColor} height={LABEL_HEIGHT} lineHeight={1.2}>
        {priceLabel}
      </Box>
      <Box
        width={BAR_WIDTH}
        display="flex"
        flexDirection="column"
        borderRadius="8px"
        overflow="hidden"
      >
        {children}
      </Box>
      <Box
        className="caption_12_b gray500"
        textAlign="center"
        height={CAPTION_HEIGHT}
        lineHeight={1.4}
        whiteSpace="pre-line"
      >
        {caption}
      </Box>
    </BaseStack>
  );
}

/**
 * @param familyPriceLabel 가족장 막대 위 라벨. 빈 문자열이면 자리만 남긴다(막대 바닥선 유지)
 * @param mubinsoPriceLabel 무빈소장 막대 위 라벨. 금액 대신 절감폭을 쓰는 화면이 있다
 * @param showAmounts false 면 상조 비용 칸의 금액을 숨긴다(E4 비용 퍼널 C-5 는 숫자 없이 비율만 보여 준다)
 */
export function MubinsoCostChart({
  familyPriceLabel = "800~1,000만원",
  mubinsoPriceLabel = "210~280만원",
  showAmounts = true,
}: {
  familyPriceLabel?: string;
  mubinsoPriceLabel?: string;
  showAmounts?: boolean;
} = {}) {
  return (
    <BaseStack width="100%" bg={colors.warmGray100} borderRadius="16px" p={20} alignItems="center">
      <BaseStack direction="horizontal" spacing={28} alignItems="flex-end">
        {/* 가족장 */}
        <Bar
          priceLabel={familyPriceLabel}
          priceLabelColor={colors.gray900}
          caption={"가족장\n(조문객 100명 기준)"}
        >
          <Segment height="34px" bg={colors.orange350} color={colors.orange800}>
            제단꽃
          </Segment>
          <Segment height="66px" bg={CHART_YELLOW} color={colors.gray900}>
            조문객 음식
          </Segment>
          <Segment height="54px" bg={CHART_SEGMENT_GRAY} color={colors.gray600}>
            빈소·시설
            <br />
            이용료
          </Segment>
          <Segment height="84px" bg={colors.orange650} color={colors.white}>
            상조 비용
            {showAmounts && (
              <Box className="subtitle_14_b" color={colors.white}>
                289만원
              </Box>
            )}
          </Segment>
        </Bar>

        {/* 무빈소장 */}
        <Bar
          priceLabel={mubinsoPriceLabel}
          priceLabelColor={colors.orange650}
          caption={"무빈소장\n "}
        >
          <Segment height="32px" bg={CHART_SEGMENT_GRAY} color={colors.gray600}>
            시설 이용료
          </Segment>
          <Segment height="64px" bg={colors.orange650} color={colors.white}>
            상조 비용
            {showAmounts && (
              <Box className="subtitle_14_b" color={colors.white}>
                159만원
              </Box>
            )}
          </Segment>
        </Bar>
      </BaseStack>
    </BaseStack>
  );
}
